import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HotToastService } from '@ngneat/hot-toast';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { NavigationRoute } from 'src/app/core/constants/constants';
import { AddShoppingCartItemRequest, ProductDto, TestSessionForRegistrationDto } from 'src/app/core/dtos/dto';
import { BrandEnum, RegWorkflowEnum } from 'src/app/core/dtos/enum';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { Step } from 'src/app/core/dtos/step';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { ProductService } from 'src/app/core/services/product.service';
import { RegWorkflowMappingService } from 'src/app/core/services/reg-workflow-mapping.service';
import { ShoppingCartService } from 'src/app/core/services/shopping-cart.service';
import { PageStepEnum, StepProgressService } from 'src/app/core/services/step-progress.service';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { selectRegTestSession } from 'src/app/state/reg-data.selectors';
import { productTypeMappping } from './product-type-mapping';
import { TopProducts } from './study-material-data';
import { StudyMaterialModalComponent } from './study-material-modal/study-material-modal.component';


@Component({
  selector: 'app-study-materials',
  templateUrl: './study-materials.component.html',
  styleUrls: ['./study-materials.component.scss']
})
export class StudyMaterialsComponent implements OnInit, OnDestroy {

  pagePath = NavigationRoute.REGISTRATION.studyMaterials;
  nextStep: string;
  regWorkFlow: RegWorkflowEnum;
  testSessionInformation$: Observable<TestSessionForRegistrationDto>;
  topProducts: TopProducts[] = [];
  steps: Step[];
  testSessionSubscription: Subscription;
  addProductSubscription: Subscription;
  checkWarningSubscription: Subscription;
  brandId: BrandEnum;
  addItemWarningMesssage: string;
  productIdToAdd: number;
  addShoppingCartItemRequest: AddShoppingCartItemRequest;

  @ViewChild('addItemWarning') addItemWarning: ModalComponent;

  constructor(
    private store: Store,
    private productService: ProductService,
    private configHelper: ConfigHelper,
    private stepProgressService: StepProgressService,
    private shoppingCartService: ShoppingCartService,
    private router: Router,
    private toastService: HotToastService,
    private modalService: NgbModal,
    private regWorkflowMappingService: RegWorkflowMappingService,
  ) {
    this.steps = this.stepProgressService.getSteps(PageStepEnum.StudyMaterials);
  }

  ngOnInit(): void {
    this.testSessionInformation$ = this.store.pipe(select(selectRegTestSession));
    this.brandId = this.configHelper.getBrandId();


    this.testSessionSubscription = this.testSessionInformation$.pipe(take(1))
      .subscribe(testSession => {
        this.getTopProducts(testSession.currencyCode, testSession.testTypeDisplayName);
        this.regWorkFlow = this.regWorkflowMappingService.getRegWorkFlow(testSession);
        this.nextStep = this.regWorkflowMappingService.getNextStepNew(this.regWorkFlow, this.pagePath);
      });
  }

  getTopProducts(currencyCode: string, testTypeName: string) {
    this.productService.getTopProducts(currencyCode, testTypeName)
      .subscribe(
        result => {
          result.content.map(item => {
            this.topProducts.push({
              id: item.id,
              name: item.name,
              listPrice: item.listPrice,
              currencyCode: item.currencyCode,
              imageUrl: this.configHelper.getProductImageUrl(item.imageUrl),
              productTypeId: item.productTypeId,
              productTypeName: productTypeMappping.find(x => x.productTypeId === item.productTypeId).productTypeName,
              needsPurchase: item.needsPurchase,
              description: item.description,
              globalIPDiscountPercentage: item.globalIPDiscountPercentage,
              discountCodeId: item.discountCodeId,
              discountPercent: item.discountPercent,
              isAllInclusivePrice: item.isAllInclusivePrice,
              priceAllInclusive: item.priceAllInclusive,
              isPurchasable: item.isPurchasable
            });
          },
            () => {
              this.toastService.warning('Unable to display study materials');
            });
        }
      );
  }

  onAdd(product: ProductDto) {
    this.addShoppingCartItemRequest = {
      productId: product.id
    };
    this.productIdToAdd = product.id;
    this.checkWarningSubscription = this.shoppingCartService.checkForWarnings(product.id)
      .subscribe(
        (result) => {
          if (result.content === "") {
            this.finalizeAdd();
          }
          else {
            if (this.modalService.hasOpenModals) {
              this.modalService.dismissAll();
            }
            this.addItemWarningMesssage = result.content;
            this.addItemWarning.open();
          }
        }
        ,
        (error: HttpErrorResponse) => {
          if (error.status !== 401) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            if (errorMessage?.reasons) {
              errorMessage.reasons.forEach(reasons => {
                this.toastService.warning(reasons.errorMessage);
              });
            }
          }
        }
      );
  }

  finalizeAdd() {
    this.addProductSubscription = this.shoppingCartService.addProduct(this.addShoppingCartItemRequest)
      .subscribe(
        result => {
          this.shoppingCartService.setShoppingCart(result);
          this.router.navigate(['/registration/order-summary']);
        },
        () => {
          this.toastService.warning('Unable to add item to cart');
        });
  }

  onModalLoad(productId: number) {
    const product = this.topProducts.find(x => x.id === productId);
    const modalRef = this.modalService.open(StudyMaterialModalComponent, { size: 'md' });
    modalRef.componentInstance.product = product;
    modalRef.componentInstance.brandId = this.brandId;
    modalRef.componentInstance.addItem.subscribe((i) => this.onAdd(i));
  }

  redirectNextStep() {
    this.router.navigate([this.nextStep]);
  }

  ngOnDestroy(): void {
    this.testSessionSubscription?.unsubscribe();
    this.addProductSubscription?.unsubscribe();
    this.checkWarningSubscription?.unsubscribe();
  }

}
